import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  $Button,
  $Select,
  $Modal,
  $Drawer,
  $Tooltip,
  $Popconfirm,
} from "us.common/components";

import { useTranslation } from "react-i18next";
import { DeleteOutlined, FileSearchOutlined, ReloadOutlined } from "us.icons";
import { SplitDocItem } from "./components";
import {
  createPreviewsFromArrayBuffer,
  detectBlankMarkers,
  generateSplitFiles,
  generatePerPageFiles,
  PreviewDescriptor,
} from "./functions";
import "./Home.scss";
import { SpliMode } from "./constants";

type Props = {
  onFinish: (files: File[]) => void;
};
interface SplitDocPreview extends PreviewDescriptor {
  pageIndex: number;
  url: string;
}

type SplitDocMode = (typeof SpliMode)[keyof typeof SpliMode];

/** Shape of the imperative handle exposed via `ref`. */
interface SplitDocumentsHandle {
  open: (file: File | { originFileObj?: File } | null | undefined) => void;
}

type SplitState = {
  docs: SplitDocPreview[];
  visible: boolean;
  markers: boolean[];
  baseName: string;
  mode: SplitDocMode;
  isDetectingBlanks: boolean;
  isCreatingSplits: boolean;
  rotations: number[];
};

const INITIAL: Readonly<SplitState> = Object.freeze({
  docs: [],
  visible: false,
  markers: [],
  baseName: "",
  mode: SpliMode.ALL,
  isDetectingBlanks: false,
  isCreatingSplits: false,
  rotations: [],
});

/**
 * SplitDocuments component provides a UI to split a multi-page PDF into parts.
 *
 * - Opens a drawer with page previews when `open(file)` is called via ref.
 * - Allows marking split points, rotating pages, deleting pages and previewing pages.
 * - Supports auto-detection of blank pages and generates split PDF files.
 *
 * @returns {JSX.Element} The rendered split documents UI (drawer + preview modal).
 */
const SplitDocuments = forwardRef<SplitDocumentsHandle, Props>(
  ({ onFinish }, ref) => {
    const { t } = useTranslation(
      ["US.COLLECTION.DOCUMENTS", "US.COLLECTION.COMMON"],
      {
        useSuspense: true,
      },
    );

    const [split, setSplit] = useState<SplitState>(INITIAL);
    const [previewPageIdx, setPreviewPageIdx] = useState<number | null>(null);
    const [blankDetectionProgress, setBlankDetectionProgress] = useState(0);
    const splitSourceBufferRef = useRef<ArrayBuffer | null>(null);
    const prevIsDetectingRef = useRef(false);

    useImperativeHandle(ref, () => ({ open: (file) => openWithFile(file) }));

    /**
     * Type guard to check if a value is a wrapped upload object with `originFileObj`.
     */
    const isUploadWrapper = (
      file: File | { originFileObj?: File },
    ): file is { originFileObj?: File } =>
      typeof file === "object" && file !== null && "originFileObj" in file;

    /**
     * Open the split UI with a PDF file.
     * - Accepts a `File` or upload object, validates it's a PDF, reads it as an
     *   ArrayBuffer, generates page previews and initializes component state.
     * - Revokes any previously created object URLs before resetting state to
     *   avoid leaking blob references across successive opens.
     * @param file File or upload object containing the PDF to split
     */
    const openWithFile = useCallback(
      async (file: File | { originFileObj?: File } | null | undefined) => {
        try {
          if (!file) return;

          const inputFile: File | undefined = isUploadWrapper(file)
            ? file.originFileObj
            : file;

          if (!inputFile) return;

          if (inputFile.type !== "application/pdf") {
            console.error(
              `Rejected file "${inputFile.name}": expected application/pdf, got "${inputFile.type}"`,
            );
            return;
          }

          const arrayBuffer = await inputFile.arrayBuffer();
          const baseName = (inputFile.name || "merged").replace(/\.pdf$/i, "");
          const previews: SplitDocPreview[] =
            await createPreviewsFromArrayBuffer(arrayBuffer);

          split.docs.forEach(
            (d: SplitDocPreview) => d.url && URL.revokeObjectURL(d.url),
          );

          splitSourceBufferRef.current = arrayBuffer;
          setSplit((prev) => ({
            ...INITIAL,
            docs: previews,
            markers: new Array(Math.max(0, previews.length - 1)).fill(false),
            rotations: new Array(previews.length).fill(0),
            baseName,
            visible: true,
          }));
        } catch (err) {
          console.error("Error preparing split UI", err);
        }
      },
      [split.docs],
    );

    /**
     * Detect blank pages among the currently loaded previews and mark split points.
     * Sets `isDetectingBlanks` while detection is in progress and updates markers.
     */
    const detectBlankPages = useCallback(async () => {
      if (split.docs.length === 0) return;

      const baselineDocsLength = split.docs.length;

      setSplit((prev) => ({ ...prev, isDetectingBlanks: true }));
      try {
        const markers = await detectBlankMarkers(split.docs);
        setSplit((prev) => {
          if (prev.docs.length !== baselineDocsLength) {
            return { ...prev, isDetectingBlanks: false };
          }
          return { ...prev, markers, isDetectingBlanks: false };
        });
      } catch (err) {
        console.error("Error detecting blank pages", err);
        setSplit((prev) => ({ ...prev, isDetectingBlanks: false }));
      }
    }, [split.docs]);

    /**
     * Handle split mode selection change.
     * - Updates the split mode in state.
     * - If BLANK mode: trigger blank page detection.
     * - If ALL mode: mark all pages for split and show detection animation.
     * - If MANUAL mode: clear all split markers.
     * @param val Selected split mode value
     */
    const handleSplitModeChange = useCallback(
      (val: SplitDocMode) => {
        const mode = val || SpliMode.MANUAL;
        setSplit((prev) => ({ ...prev, mode }));
        if (mode === SpliMode.BLANK) {
          detectBlankPages();
        } else if (mode === SpliMode.ALL) {
          setSplit((prev) => ({
            ...prev,
            markers: new Array(Math.max(0, prev.docs.length - 1)).fill(true),
            isDetectingBlanks: true,
          }));
          setTimeout(() => {
            setSplit((prev) => ({
              ...prev,
              isDetectingBlanks: false,
            }));
          }, 700);
        } else {
          setSplit((prev) => ({
            ...prev,
            markers: new Array(Math.max(0, prev.docs.length - 1)).fill(false),
          }));
        }
      },
      [split.docs.length, detectBlankPages],
    );

    /**
     * Close the split drawer, revoke any object URLs created for previews,
     * reset component state and clear the source buffer.
     */
    const closeSplitDrawer = useCallback(() => {
      split.docs.forEach(
        (d: SplitDocPreview) => d.url && URL.revokeObjectURL(d.url),
      );
      setSplit(INITIAL);
      setPreviewPageIdx(null);
      splitSourceBufferRef.current = null;
    }, [split.docs]);

    /**
     * Create split PDF files based on the current markers and rotations.
     * - Uses the original ArrayBuffer stored in `splitSourceBufferRef` and
     *   the `generateSplitFiles` helper to produce File objects.
     * - Resets state and invokes `onFinish` with generated files when done.
     */
    const createSplitPdfs = useCallback(async () => {
      const { mode, docs, markers, rotations, baseName } = split;
      const sourceBuffer = splitSourceBufferRef.current;

      if (!sourceBuffer) return;

      setSplit((prev) => ({ ...prev, isCreatingSplits: true }));
      try {
        let generated: File[] = [];
        if (mode === SpliMode.ALL) {
          generated = await generatePerPageFiles(
            sourceBuffer,
            rotations,
            docs,
            baseName,
          );
        } else {
          generated = await generateSplitFiles(
            sourceBuffer,
            markers,
            rotations,
            docs,
            baseName,
          );
        }

        docs.forEach(
          (d: SplitDocPreview) => d.url && URL.revokeObjectURL(d.url),
        );

        setSplit(INITIAL);
        setPreviewPageIdx(null);
        splitSourceBufferRef.current = null;

        if (generated.length) onFinish(generated);
      } catch (err) {
        console.error("Error creating split PDFs", err);
        setSplit((prev) => ({ ...prev, isCreatingSplits: false }));
      }
    }, [split, onFinish]);

    /**
     * Toggle the split marker at the specified page index.
     * @param index Index of the marker to toggle
     */
    const toggleSplitMarker = useCallback(
      (index: number) =>
        setSplit((prev) => {
          const markers = [...prev.markers];
          markers[index] = !markers[index];
          return { ...prev, markers };
        }),
      [],
    );

    /**
     * Rotate the page at `index` by 90 degrees clockwise.
     * Rotation cycles through 0 -> 90 -> 180 -> 270 -> 0.
     * @param index Page index to rotate
     */
    const toggleRotate = useCallback(
      (index: number) =>
        setSplit((prev) => {
          const rotations = [...prev.rotations];
          rotations[index] = ((rotations[index] ?? 0) + 90) % 360;
          return { ...prev, rotations };
        }),
      [],
    );

    /**
     * Delete the preview page at the given index.
     * - Revokes any object URL for the removed page, removes the page from
     *   the `docs` array and adjusts `markers` and `rotations` accordingly.
     * - Also adjusts the current preview index when necessary.
     * @param index Page index to remove
     */
    const deleteSplitPage = useCallback((index: number) => {
      setSplit((prev) => {
        if (prev.docs[index]?.url) URL.revokeObjectURL(prev.docs[index].url);
        const docs = prev.docs.filter(
          (_: SplitDocPreview, i: number) => i !== index,
        );
        const rotations = prev.rotations.filter(
          (_: number, i: number) => i !== index,
        );
        let markers = [...prev.markers];
        if (markers.length > 0) {
          const markerToRemove = Math.max(
            0,
            index - 1 < markers.length ? index - 1 : markers.length - 1,
          );
          markers = markers.filter(
            (_: boolean, i: number) => i !== (index === 0 ? 0 : markerToRemove),
          );
        }
        while (markers.length > Math.max(0, docs.length - 1)) markers.pop();
        while (markers.length < Math.max(0, docs.length - 1))
          markers.push(false);
        return { ...prev, docs, markers, rotations };
      });

      setPreviewPageIdx((prev) => {
        if (prev === null) return null;
        if (prev === index) return null;
        if (prev > index) return prev - 1;
        return prev;
      });
    }, []);

    /** Close the modal page preview. */
    const closePagePreview = useCallback(() => setPreviewPageIdx(null), []);
    /** Navigate the preview modal to the previous page, if available. */
    const goToPrevPage = useCallback(
      () =>
        setPreviewPageIdx((prev) =>
          prev !== null && prev > 0 ? prev - 1 : prev,
        ),
      [],
    );
    /** Navigate the preview modal to the next page, if available. */
    const goToNextPage = useCallback(
      () =>
        setPreviewPageIdx((prev) =>
          prev !== null && prev < split.docs.length - 1 ? prev + 1 : prev,
        ),
      [split.docs.length],
    );

    /**
     * Drives the blank-detection progress bar.
     * - While detecting: ramps progress up toward 90% at a smooth, steady pace.
     * - On completion (transition from detecting -> not detecting): briefly
     *   animates the bar up to 100% so the user sees a clear success state,
     *   then resets to 0 after a short pause.
     * - `clearInterval` is only ever called from the effect's cleanup or from
     *   a plain local counter in the closure — never from inside the
     *   functional state updater — to avoid relying on React's state batching
     *   timeline for control flow.
     */
    useEffect(() => {
      const wasDetecting = prevIsDetectingRef.current;
      prevIsDetectingRef.current = split.isDetectingBlanks;

      if (split.isDetectingBlanks) {
        setBlankDetectionProgress(0);

        const iv = setInterval(() => {
          setBlankDetectionProgress((p) =>
            Math.min(90, p + (1 + Math.random() * 4)),
          );
        }, 100);

        return () => clearInterval(iv);
      }

      if (wasDetecting) {
        let ticks = 0;
        const maxTicks = 8;

        const iv = setInterval(() => {
          ticks += 1;
          setBlankDetectionProgress((p) => Math.min(100, p + 12));
          if (ticks >= maxTicks) clearInterval(iv);
        }, 40);

        const resetTimeout = setTimeout(
          () => {
            setBlankDetectionProgress(0);
          },
          maxTicks * 40 + 250,
        );

        return () => {
          clearInterval(iv);
          clearTimeout(resetTimeout);
        };
      }
    }, [split.isDetectingBlanks]);

    const previewRotation =
      previewPageIdx !== null ? (split.rotations[previewPageIdx] ?? 0) : 0;
    const iframeTransform =
      previewRotation === 90 || previewRotation === 270
        ? `rotate(${previewRotation}deg) scale(0.73)`
        : `rotate(${previewRotation}deg)`;

    return (
      <$Drawer
        title={t("US.COMMON:COMMON.SPLIT_MERGE_PDF")}
        width="90%"
        open={split.visible}
        onClose={closeSplitDrawer}
        className="split-merge-drawer"
        destroyOnClose
      >
        <>
          {split.isDetectingBlanks && (
            <div className="split-detect-overlay">
              <div className="split-detect-icon">
                <FileSearchOutlined style={{ fontSize: 20 }} />
              </div>

              <div className="split-detect-label-group">
                <span className="split-detect-label">
                  {t("US.COMMON:COMMON.DETECTING_BLANK_PAGES")}
                </span>
                <span className="split-detect-sub">
                  {t("US.COMMON:COMMON.DETECTING_BLANK_PAGES_MESSAGE")}
                </span>
              </div>

              <div className="split-detect-progress-track">
                <div
                  className="split-detect-progress-fill"
                  style={{ width: `${Math.round(blankDetectionProgress)}%` }}
                />
              </div>
            </div>
          )}

          <div className="split-previews">
            {split.docs.map((page: SplitDocPreview, idx: number) => (
              <SplitDocItem
                key={`${page.pageIndex}-${idx}`}
                page={page}
                idx={idx}
                isLast={idx === split.docs.length - 1}
                markerActive={split.markers[idx]}
                onToggle={toggleSplitMarker}
                onDelete={deleteSplitPage}
                onRotate={toggleRotate}
                onPreview={setPreviewPageIdx}
                rotation={split.rotations[idx] ?? 0}
                previewNotSupported={t("US.COMMON:COMMO.PREVIEW_NOT_SUPPORTED")}
                deleteLabel={t("US.COMMON:COMMON.DELETE")}
                confirmDeleteLabel={t("US.COMMON:COMMO.SURE_TO_DELETE")}
                yesLabel={t("US.COMMON:COMMON.YES")}
                noLabel={t("US.COMMON:COMMON.NO")}
                rotateLabel={t("US.COMMON:COMMON.ROTATE")}
                splitLabel={t("US.COMMON:COMMON.SPLIT")}
              />
            ))}
          </div>

          <div className="drawer-footer-fixed align-content-center justify-content-end">
            <$Select
              size="small"
              name="splitOptions"
              formitem={{
                label: t("US.COMMON:COMMON:SPLIT_METHOD"),
              }}
              allOption={false}
              value={split.mode}
              onChange={handleSplitModeChange}
              className="split-select"
              options={[
                {
                  label: t("US.COMMON:COMMON.SPLIT_EVERY_PAGE"),
                  value: SpliMode.ALL,
                },
                {
                  label: t("US.COMMON:COMMON.SPLIT_BY_MANUALLY"),
                  value: SpliMode.MANUAL,
                },
                {
                  label: t("US.COMMON:COMMON.SPLIT_BY_BLANK_PAGE"),
                  value: SpliMode.BLANK,
                },
              ]}
              optionValue="value"
              optionText="label"
              data-testid="split-options"
              popupClassName="split-select-autocomplete-popup"
              getPopupContainer={() => document.body}
              disabled={split.isDetectingBlanks}
            />

            <$Button
              onClick={closeSplitDrawer}
              className="mr-2 ml-2"
              disabled={split.isDetectingBlanks}
            >
              {t("US.COMMON:COMMON.CANCEL")}
            </$Button>

            <$Button
              type="primary"
              onClick={createSplitPdfs}
              disabled={split.isDetectingBlanks}
              loading={split.isCreatingSplits}
            >
              {t("US.COMMON:COMMON.CREATE_SPLIT_PDFS")}
            </$Button>
          </div>

          {previewPageIdx !== null && split.docs[previewPageIdx] && (
            <$Modal
              open
              onCancel={closePagePreview}
              width={600}
              centered
              destroyOnClose
              footer={null}
              title={
                <div className="modal-title-container">
                  <div className="modal-preview-badge">
                    {previewPageIdx + 1}
                  </div>

                  <span className="modal-title-text">
                    {t("US.COMMON:COMMON.PAGE_PREVIEW")}
                    &nbsp;
                    <span className="modal-title-sub">
                      {previewPageIdx + 1} / {split.docs.length}
                    </span>
                  </span>
                </div>
              }
            >
              <div className="modal-preview-container">
                <iframe
                  key={`modal-preview-${previewPageIdx}`}
                  title={`modal-preview-${previewPageIdx}`}
                  src={`${split.docs[previewPageIdx].url}#toolbar=0&navpanes=0&scrollbar=0`}
                  className="modal-preview-iframe"
                  style={{ transform: iframeTransform }}
                />
              </div>

              <div className="modal-preview-footer">
                <div className="modal-preview-footer-left">
                  <$Button
                    size="small"
                    onClick={goToPrevPage}
                    disabled={previewPageIdx === 0}
                  >
                    {t("US.COMMON:COMMON.PREVIOUS")}
                  </$Button>

                  <span className="modal-preview-counter">
                    {previewPageIdx + 1} / {split.docs.length}
                  </span>

                  <$Button
                    size="small"
                    onClick={goToNextPage}
                    disabled={previewPageIdx === split.docs.length - 1}
                  >
                    {t("US.COMMON:COMMON.NEXT")}
                  </$Button>
                </div>

                <div className="modal-preview-footer-right">
                  {(split.rotations[previewPageIdx] ?? 0) !== 0 && (
                    <span className="modal-rotation-badge">
                      {split.rotations[previewPageIdx]}°
                    </span>
                  )}

                  <$Tooltip title={t("US.COMMON:COMMON.ROTATE")}>
                    <$Button
                      size="small"
                      onClick={() =>
                        previewPageIdx !== null && toggleRotate(previewPageIdx)
                      }
                      icon={
                        <ReloadOutlined
                          style={{
                            display: "block",
                            transform: "scaleX(-1)",
                            fontSize: 13,
                          }}
                        />
                      }
                    />
                  </$Tooltip>

                  <$Popconfirm
                    title={t("US.COMMON:COMMON.SURE_TO_DELETE")}
                    onConfirm={() => {
                      deleteSplitPage(previewPageIdx);
                      closePagePreview();
                    }}
                    okText={t("US.COMMON:COMMON.YES")}
                    cancelText={t("US.COMMON:COMMON.NO")}
                  >
                    <$Button size="small" danger icon={<DeleteOutlined />} />
                  </$Popconfirm>
                </div>
              </div>
            </$Modal>
          )}
        </>
      </$Drawer>
    );
  },
);

export default SplitDocuments;
